import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardRoundedIcon from '@material-ui/icons/ArrowForwardRounded';
import HomeRoundedIcon from '@material-ui/icons/HomeRounded';
import Container from '../Elements/Container';
import UserContext from '../../context/UserContext';
import useDeviceDetect from '../../hooks/useDeviceDetect';
import backgroundInicio from './assets/background_inicio.png';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
    paddingTop: 48,
    [theme.breakpoints.down('sm')]: {
      minHeight: 'calc(100dvh - 48px)',
      paddingTop: 0,
    },
  },

  hero: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 2,
    color: '#fff',
    background: 'linear-gradient(130deg, #005f9f 0%, #0d88c9 52%, #31b8e8 100%)',
    padding: theme.spacing(4, 3),
    boxShadow: '0 24px 40px rgba(0, 87, 140, 0.28)',
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 1.75),
      marginTop: '50px'
    },
  },

  heroGlowOne: {
    position: 'absolute',
    top: -80,
    right: -60,
    width: 260,
    height: 260,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0) 70%)',
    pointerEvents: 'none',
  },

  heroGlowTwo: {
    position: 'absolute',
    bottom: -120,
    left: -80,
    width: 320,
    height: 320,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(0,56,99,0.5) 0%, rgba(0,56,99,0) 75%)',
    pointerEvents: 'none',
  },

  heroContent: {
    position: 'relative',
    zIndex: 1,
  },

  heroTopRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.25),
  },

  heroLogoBadge: {
    padding: theme.spacing(0.55, 1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  heroLogoDesktop: {
    position: 'absolute',
    top: 0,
    right: 0,
  },

  heroLogo: {
    height: 22,
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      height: 18,
    },
  },

  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.35),
    minWidth: 0,
  },

  logoMark: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.45)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    marginRight: theme.spacing(0.35),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      width: 34,
      height: 34,
      borderRadius: 9,
      marginRight: theme.spacing(0.2),
    },
  },

  heroTitle: {
    fontSize: 36,
    lineHeight: 1.08,
    letterSpacing: -0.4,
    marginBottom: theme.spacing(1.2),
    maxWidth: 760,
    [theme.breakpoints.down('sm')]: {
      fontSize: 23,
      lineHeight: 1.12,
      marginBottom: theme.spacing(0.75),
      maxWidth: '100%',
    },
  },

  heroSubtitle: {
    opacity: 0.95,
    fontSize: 16,
    lineHeight: 1.45,
    maxWidth: 720,
    [theme.breakpoints.down('sm')]: {
      fontSize: 13,
      lineHeight: 1.35,
      maxWidth: '100%',
    },
  },

  heroButtons: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1.2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.6),
      gap: theme.spacing(0.8),
      flexWrap: 'nowrap',
    },
  },

  primaryBtn: {
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    padding: theme.spacing(1.1, 2.1),
    fontWeight: 700,
    textTransform: 'none',
    color: 'rgba(248, 250, 252, 0.92)',
    background: 'linear-gradient(135deg, #64748b 0%, #334155 100%)',
    border: '1px solid rgba(255,255,255,0.16)',
    boxShadow: '0 14px 30px rgba(10, 16, 28, 0.28)',
    transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease, border-color 160ms ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #55657d 0%, #243244 100%)',
      borderColor: 'rgba(255,255,255,0.24)',
      boxShadow: '0 18px 34px rgba(10, 16, 28, 0.34)',
      transform: 'translateY(-1px)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.7, 1.2),
      minHeight: 36,
      fontSize: 12,
      flex: 1,
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
  },

  secondaryBtn: {
    borderRadius: theme.borderRadiusBoxes?.borderRadius || 4,
    padding: theme.spacing(1.1, 2),
    textTransform: 'none',
    fontWeight: 600,
    color: 'rgba(248, 250, 252, 0.92)',
    borderColor: 'rgba(255,255,255,0.28)',
    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
    boxShadow: '0 12px 26px rgba(5, 37, 58, 0.18)',
    transition: 'transform 160ms ease, box-shadow 160ms ease, background 160ms ease, border-color 160ms ease',
    '&:hover': {
      borderColor: 'rgba(255,255,255,0.4)',
      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      boxShadow: '0 16px 30px rgba(5, 37, 58, 0.24)',
      transform: 'translateY(-1px)',
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.7, 1.1),
      minHeight: 36,
      fontSize: 12,
      flex: 1,
      whiteSpace: 'nowrap',
      minWidth: 0,
    },
  },

  staticPreview: {
    position: 'relative',
    flex: '1 1 0',
    minHeight: 0,
    marginTop: 0,
    borderRadius: 2,
    overflow: 'hidden',
    border: '1px solid rgba(22, 85, 122, 0.45)',
    backgroundImage: `url(${backgroundInicio})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundColor: '#0f1c25',
    boxShadow: '0 18px 40px rgba(9, 31, 45, 0.34)',
  },

  staticOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, rgba(6, 16, 24, 0.3) 0%, rgba(6, 16, 24, 0.72) 100%)',
  },

  staticContent: {
    position: 'relative',
    zIndex: 2,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(2, 2.5),
    color: '#fff',
  },

  staticLogo: {
    width: 400,
    maxWidth: '78%',
    [theme.breakpoints.down('xs')]: {
      width: 270,
      marginBottom: theme.spacing(1),
    },
  },

  staticSubtitle: {
    color: 'rgb(255, 255, 255)',
    fontSize: 30,
    lineHeight: 1.34,
    fontWeight: 700,
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
      lineHeight: 1.28,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 15,
    },
  },

  staticDescription: {
    marginTop: theme.spacing(1.1),
    color: 'rgb(255, 255, 255)',
    fontSize: 20.5,
    fontWeight: 600,
    [theme.breakpoints.down('sm')]: {
      fontSize: 15.5,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: 13.5,
    },
  }
}));

const Landing = () => {
  const classes = useStyles();
  const history = useHistory();
  const { plan } = useContext(UserContext);
  const { isMobile } = useDeviceDetect();

  return (
    <>
      <Container>
        <Box className={classes.root}>
          <Card className={classes.hero} elevation={0}>
            <Box className={classes.heroGlowOne} />
            <Box className={classes.heroGlowTwo} />

            <Box className={classes.heroContent}>
              <Box className={classes.heroTopRow}>
                <Box className={classes.brandRow}>
                  <Box className={classes.logoMark}>
                    <HomeRoundedIcon fontSize='small' />
                  </Box>
                </Box>

                {isMobile && (
                  <Box className={classes.heroLogoBadge}>
                    <img src='/images/logo2.svg' alt='Powermate' className={classes.heroLogo} />
                  </Box>
                )}
              </Box>

              {!isMobile && (
                <Box className={`${classes.heroLogoBadge} ${classes.heroLogoDesktop}`}>
                  <img src='/images/logo2.svg' alt='Powermate' className={classes.heroLogo} />
                </Box>
              )}

              <Typography className={classes.heroTitle}>
                Gestión inteligente de energía
              </Typography>

              <Typography className={classes.heroSubtitle}>
                {isMobile
                  ? 'Controlá y optimizá el consumo energético desde un solo lugar.'
                  : 'Supervisa, analiza y optimiza el consumo energético de tu empresa desde un solo lugar.'}
              </Typography>

              <Box className={classes.heroButtons}>
                <Button
                  className={classes.primaryBtn}
                  variant='contained'
                  color='default'
                  endIcon={isMobile ? null : <ArrowForwardRoundedIcon />}
                  onClick={() => history.push('/resumen')}
                  disabled={!plan?.access?.dashboards_resumen}
                >
                  {isMobile ? 'Dashboard resumen' : 'Ir a Dashboard Resumen'}
                </Button>

                <Button
                  className={classes.secondaryBtn}
                  variant='outlined'
                  onClick={() => history.push('/configuracion')}
                >
                  Ir a Configuración
                </Button>
              </Box>
            </Box>
          </Card>

          <Box className={classes.staticPreview}>
            <Box className={classes.staticOverlay} />

            <Box className={classes.staticContent}>
              <img src='/images/logo-horizontal-fondo-oscuro.png' alt='Powermate' className={classes.staticLogo} />

              <Typography className={classes.staticSubtitle}>
                La plataforma inteligente para gestionar tu energía
              </Typography>

              <Typography className={classes.staticDescription}>
                Controla, optimiza y automatiza el consumo energético de tu empresa en tiempo real.
              </Typography>
            </Box>
          </Box>

        </Box>
      </Container>
    </>
  );
};

export default Landing;
